import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Store } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { isMarketplaceProfileComplete } from "@shared/schema";
import type { MarketplaceProfile } from "@shared/schema";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

type FormState = {
  displayName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  stateCode: string;
  bio: string;
};

const EMPTY: FormState = {
  displayName: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  stateCode: "",
  bio: "",
};

function profileToForm(p: MarketplaceProfile | null | undefined): FormState {
  if (!p) return EMPTY;
  return {
    displayName: p.displayName ?? "",
    phone: p.phone ?? "",
    email: p.email ?? "",
    address: p.address ?? "",
    city: p.city ?? "",
    stateCode: p.stateCode ?? "",
    bio: p.bio ?? "",
  };
}

/**
 * Blocking form shown when a dealer tries to access the marketplace without a
 * complete profile. All fields are required EXCEPT the short bio. The dealer
 * either completes setup (gaining access) or declines and forfeits the
 * marketplace by navigating away.
 */
export function MarketplaceProfileForm({
  profile,
  onCompleted,
  onForfeit,
}: {
  profile: MarketplaceProfile | null | undefined;
  onCompleted: () => void;
  onForfeit: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(() => profileToForm(profile));

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const missing =
    !form.displayName.trim() ||
    !form.phone.trim() ||
    !form.email.trim() ||
    !form.address.trim() ||
    !form.city.trim() ||
    !form.stateCode.trim();

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/marketplace/settings/profile", {
        displayName: form.displayName.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        stateCode: form.stateCode || null,
        bio: form.bio.trim() || null,
        // Preserve existing visibility prefs (or sensible defaults for new profiles).
        showPhone: profile?.showPhone ?? false,
        showEmail: profile?.showEmail ?? false,
        showAddress: profile?.showAddress ?? true,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to save");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/settings/profile"] });
      toast({ title: t("marketplace:profileGate.profileCreated"), description: t("marketplace:profileGate.fullAccess") });
      onCompleted();
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:profileGate.couldNotSave"), description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open modal>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-violet-500" />
            {t("marketplace:profileGate.formTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("marketplace:profileGate.formDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="gate-displayName">{t("marketplace:profileGate.businessName")}</Label>
            <Input
              id="gate-displayName"
              data-testid="input-gate-display-name"
              placeholder={t("marketplace:profileGate.displayNamePlaceholder")}
              value={form.displayName}
              onChange={(e) => set("displayName", e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gate-phone">{t("marketplace:profileGate.phone")}</Label>
              <Input
                id="gate-phone"
                data-testid="input-gate-phone"
                type="tel"
                placeholder="(555) 000-0000"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                maxLength={20}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gate-email">Email *</Label>
              <Input
                id="gate-email"
                data-testid="input-gate-email"
                type="email"
                placeholder="contact@yourlot.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                maxLength={200}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gate-address">{t("marketplace:profileGate.streetAddress")}</Label>
            <Input
              id="gate-address"
              data-testid="input-gate-address"
              placeholder={t("marketplace:profileGate.addressPlaceholder")}
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gate-city">City *</Label>
              <Input
                id="gate-city"
                data-testid="input-gate-city"
                placeholder="City"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gate-state">State *</Label>
              <select
                id="gate-state"
                data-testid="select-gate-state"
                value={form.stateCode}
                onChange={(e) => set("stateCode", e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">{t("marketplace:profileGate.selectState")}</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gate-bio">{t("marketplace:profileGate.shortBio")}</Label>
            <Textarea
              id="gate-bio"
              data-testid="input-gate-bio"
              placeholder={t("marketplace:profileGate.bioPlaceholder")}
              value={form.bio}
              onChange={(e) => set("bio", e.target.value)}
              maxLength={500}
              className="min-h-[70px]"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            data-testid="button-gate-forfeit"
            onClick={onForfeit}
            disabled={saveMutation.isPending}
          >
            {t("marketplace:profileGate.notNow")}
          </Button>
          <Button
            data-testid="button-gate-save"
            onClick={() => saveMutation.mutate()}
            disabled={missing || saveMutation.isPending}
            className="bg-violet-500 hover:bg-violet-600 text-white min-w-[140px]"
          >
            {saveMutation.isPending ? "Saving…" : "Save & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Wraps every marketplace page. Renders the page only when the dealer's
 * marketplace profile is complete; otherwise it blocks the page with the
 * required setup form. Declining navigates the dealer away (forfeit).
 */
export function MarketplaceProfileGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(["marketplace", "common"]);
  const [, navigate] = useLocation();

  const { data: profile, isLoading } = useQuery<MarketplaceProfile | null>({
    queryKey: ["/api/marketplace/settings/profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/settings/profile");
      return res.json();
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isMarketplaceProfileComplete(profile)) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
      {t("marketplace:profileGate.gateLoading")}
      <MarketplaceProfileForm
        profile={profile}
        onCompleted={() => {
          // Profile query is invalidated on save; gate re-renders with children.
        }}
        onForfeit={() => navigate("/")}
      />
    </div>
  );
}

/**
 * First-visit, one-time prompt inviting a dealer to set up their marketplace
 * profile. Shown once per user (tracked in localStorage). Dealers can set up
 * now or decline — declining forfeits marketplace access until they revisit it.
 */
export function MarketplaceIntroPopup({ user }: { user: { id?: string; role?: string } | null | undefined }) {
  const { t } = useTranslation(["marketplace", "common"]);
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);

  const isDealer =
    !!user && (user.role === "subscriber" || user.role === "demo" || user.role === "owner");
  const storageKey = user?.id ? `dm_marketplace_intro_seen_${user.id}` : null;

  const { data: profile } = useQuery<MarketplaceProfile | null>({
    queryKey: ["/api/marketplace/settings/profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/settings/profile");
      return res.json();
    },
    enabled: isDealer,
    retry: false,
  });

  useEffect(() => {
    if (!isDealer || !storageKey) return;
    if (profile === undefined) return; // wait for the profile query
    if (isMarketplaceProfileComplete(profile)) return; // already set up
    if (localStorage.getItem(storageKey) === "true") return; // already prompted
    setOpen(true);
  }, [isDealer, storageKey, profile]);

  function dismiss() {
    if (storageKey) localStorage.setItem(storageKey, "true");
    setOpen(false);
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-marketplace-intro">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-violet-500" />
            {t("marketplace:profileGate.introTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("marketplace:profileGate.introDescription")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" data-testid="button-intro-later" onClick={dismiss}>
            {t("marketplace:profileGate.maybeLater")}
          </Button>
          <Button
            data-testid="button-intro-setup"
            onClick={() => { dismiss(); navigate("/marketplace"); }}
            className="bg-violet-500 hover:bg-violet-600 text-white"
          >
            {t("marketplace:profileGate.setUpNow")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
